/**
 * ICSC Pricing Service
 * API client for pricing calculations and management
 */
import axios from './axiosClient';

const API_URL = import.meta.env.VITE_API_URL;
const PRICING_API_BASE = `${API_URL}/pricing`;

/**
 * Get complete pricing summary for a certification body
 */
export const getPricingSummary = async (certBodyId) => {
  const response = await axios.get(`${PRICING_API_BASE}/summary/${certBodyId}`);
  return response.data;
};

/**
 * Get certificate pricing for all certificate types
 */
export const getCertificatePricing = async () => {
  const response = await axios.get(`${PRICING_API_BASE}/certificates`);
  return response.data;
};

/**
 * Calculate total annual cost
 */
export const calculatePricing = async (params) => {
  const response = await axios.post(`${PRICING_API_BASE}/calculate`, params);
  return response.data;
};

/**
 * Calculate certificate fee
 */
export const calculateCertificateFee = async (certificateType, quantity = 1) => {
  const response = await axios.post(`${PRICING_API_BASE}/certificate-fee`, {
    certificate_type: certificateType,
    quantity
  });
  return response.data;
};

/**
 * Create checkout session
 */
export const createCheckoutSession = async (params) => {
  const response = await axios.post(`${PRICING_API_BASE}/checkout`, params);
  return response.data;
};

/**
 * Validate CPD access
 */
export const validateCPDAccess = async (certBodyId) => {
  const response = await axios.get(`${PRICING_API_BASE}/validate/cpd-access/${certBodyId}`);
  return response.data;
};

/**
 * Validate certificate payment status
 */
export const validateCertificatePayment = async (certificateId, certificateType) => {
  const response = await axios.get(
    `${PRICING_API_BASE}/validate/certificate-payment/${certificateId}?certificate_type=${certificateType}`
  );
  return response.data;
};

/**
 * Get membership details
 */
export const getMembershipDetails = async (certBodyId) => {
  const response = await axios.get(`${PRICING_API_BASE}/membership/${certBodyId}`);
  return response.data;
};

/**
 * Get CPD subscription details
 */
export const getCPDSubscriptionDetails = async (certBodyId) => {
  const response = await axios.get(`${PRICING_API_BASE}/cpd/${certBodyId}`);
  return response.data;
};

/**
 * Pricing constants (in GBP)
 */
export const PRICING_CONSTANTS = {
  MEMBERSHIP_ANNUAL_FEE: 500,
  ISO_STANDARD_FEE: 500,
  ISO_STANDARD_FEE_DISCOUNTED: 275,
  ISO_DISCOUNT_THRESHOLD: 4,
  INITIAL_ASSESSMENT_FEE: 400,
  AUDITOR_COURSE_FEE: 300,
  CPD_ANNUAL_FEE: 1000,
  PCAF_ANNUAL_FEE: 1000,
  CERTIFICATE_FEE_ISO: 25,
  CERTIFICATE_FEE_CPD: 15,
  CERTIFICATE_FEE_COMPANY_ASSESSMENT: 15,
  CERTIFICATE_FEE_PCAF: 50,
  CURRENCY: 'GBP'
};

/**
 * Calculate ISO discount percentage
 */
export const calculateISODiscount = (isoCount) => {
  if (isoCount < PRICING_CONSTANTS.ISO_DISCOUNT_THRESHOLD) {
    return 0;
  }
  const discountedCount = isoCount - (PRICING_CONSTANTS.ISO_DISCOUNT_THRESHOLD - 1);
  const discountPerISO = PRICING_CONSTANTS.ISO_STANDARD_FEE - PRICING_CONSTANTS.ISO_STANDARD_FEE_DISCOUNTED;
  return discountPerISO * discountedCount;
};

/**
 * Format currency (GBP)
 */
export const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP'
  }).format(amount);
};

export default {
  getPricingSummary,
  getCertificatePricing,
  calculatePricing,
  calculateCertificateFee,
  createCheckoutSession,
  validateCPDAccess,
  validateCertificatePayment,
  getMembershipDetails,
  getCPDSubscriptionDetails,
  PRICING_CONSTANTS,
  calculateISODiscount,
  formatCurrency
};
